import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type CreateLocationInput,
  createLocationSchema,
  formatUsPhone,
  LOCATION_TYPES,
  type LocationType,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Warehouse } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface LocationRow {
  id: string
  key: string
  name: string
  type: LocationType
  address: string | null
  phone: string | null
  note: string | null
  isActive: boolean
  offersPickup: boolean
  offersShipping: boolean
}

const TYPE_LABEL: Record<LocationType, string> = {
  warehouse: 'Warehouse',
  store: 'Store',
  kiosk: 'Kiosk',
  repair_bench: 'Repair bench',
}

/** Lowercase, dash-separated suggestion from a name — the key stays editable until submit. */
function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

export function LocationsPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<LocationRow | null>(null)
  const canWrite = can('inventory:write')

  const { data: locations, isLoading } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<LocationRow[]>('/locations'),
  })

  const toggleActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      api.patch(`/locations/${input.id}`, { isActive: input.isActive }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.locations() })
      const previous = queryClient.getQueryData<LocationRow[]>(queryKeys.locations())
      queryClient.setQueryData<LocationRow[]>(queryKeys.locations(), (old) =>
        old?.map((l) => (l.id === input.id ? { ...l, isActive: input.isActive } : l)),
      )
      return { previous }
    },
    onError: (error, _input, context) => {
      queryClient.setQueryData(queryKeys.locations(), context?.previous)
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.locations() }),
  })

  /**
   * Opening a location to the website, or closing it again.
   *
   * Optimistic like the active switch above, because the answer is a single
   * boolean the server will almost always agree with, and a toggle that waits
   * for a round trip before moving reads as broken.
   */
  const setFulfillment = useMutation({
    mutationFn: (input: { id: string; offersPickup?: boolean; offersShipping?: boolean }) => {
      const { id, ...patch } = input
      return api.patch(`/locations/${id}`, patch)
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.locations() })
      const previous = queryClient.getQueryData<LocationRow[]>(queryKeys.locations())
      queryClient.setQueryData<LocationRow[]>(queryKeys.locations(), (old) =>
        old?.map((l) => (l.id === input.id ? { ...l, ...input } : l)),
      )
      return { previous }
    },
    onError: (error, _input, context) => {
      queryClient.setQueryData(queryKeys.locations(), context?.previous)
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.locations() }),
  })

  return (
    <Page>
      <PageHeader
        title="Locations"
        description="Warehouses, stores, kiosks and repair benches. Stock and devices move between these."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus /> New location
            </Button>
          ) : null
        }
      />

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Name
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Type
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Address
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Phone
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Sells online
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Active
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : locations && locations.length > 0 ? (
                locations.map((location) => (
                  <tr key={location.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">
                      <p className="font-medium">{location.name}</p>
                      <p className="font-identifier text-xs text-foreground-muted">
                        {location.key}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      <Badge>{TYPE_LABEL[location.type]}</Badge>
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">{location.address ?? '—'}</td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {location.phone ? formatUsPhone(location.phone) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 text-xs text-foreground-muted">
                          <Switch
                            id={`pickup-${location.id}`}
                            checked={location.offersPickup}
                            disabled={!canWrite}
                            onCheckedChange={(checked) =>
                              setFulfillment.mutate({ id: location.id, offersPickup: checked })
                            }
                            aria-label={`Collection from ${location.name}`}
                          />
                          <label htmlFor={`pickup-${location.id}`}>Collection</label>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-foreground-muted">
                          <Switch
                            id={`shipping-${location.id}`}
                            checked={location.offersShipping}
                            disabled={!canWrite}
                            onCheckedChange={(checked) =>
                              setFulfillment.mutate({ id: location.id, offersShipping: checked })
                            }
                            aria-label={`Delivery from ${location.name}`}
                          />
                          <label htmlFor={`shipping-${location.id}`}>Delivery</label>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={location.isActive}
                        disabled={!canWrite}
                        onCheckedChange={(checked) =>
                          toggleActive.mutate({ id: location.id, isActive: checked })
                        }
                        aria-label={`${location.isActive ? 'Deactivate' : 'Activate'} ${location.name}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {canWrite ? (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(location)}>
                          Edit
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Warehouse}
                      title="No locations yet"
                      description="Add a warehouse, store, kiosk or repair bench to start moving stock and devices."
                      action={
                        canWrite
                          ? { label: 'Add a location', onClick: () => setCreating(true) }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateLocationDialog open={creating} onOpenChange={setCreating} />
      <EditLocationDialog location={editing} onClose={() => setEditing(null)} />
    </Page>
  )
}

type FormValues = z.input<typeof createLocationSchema>

function CreateLocationDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [keyEdited, setKeyEdited] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues, unknown, CreateLocationInput>({
    // The exact schema the API validates against: the form cannot accept
    // something the server will then reject.
    resolver: zodResolver(createLocationSchema),
    defaultValues: { type: 'store', isActive: true },
  })

  const type = watch('type')
  const name = watch('name')

  useEffect(() => {
    if (!open) {
      reset({ type: 'store', isActive: true })
      setKeyEdited(false)
    }
  }, [open, reset])

  // Suggest a key from the name until the person edits it directly.
  useEffect(() => {
    if (!keyEdited) setValue('key', slugify(name ?? ''))
  }, [name, keyEdited, setValue])

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post('/locations', values)
      await queryClient.invalidateQueries({ queryKey: queryKeys.locations() })
      toast.success('Location created')
      reset()
      onOpenChange(false)
    } catch (error) {
      toast.error(
        'Could not create the location',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New location</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <Field label="Name" error={errors.name?.message} required>
              {(props) => <Input {...props} {...register('name')} invalid={!!errors.name} />}
            </Field>

            <Field
              label="Key"
              error={errors.key?.message}
              required
              hint="Used internally to identify this location. Cannot be changed later."
            >
              {(props) => (
                <Input
                  {...props}
                  {...register('key', { onChange: () => setKeyEdited(true) })}
                  identifier
                  invalid={!!errors.key}
                />
              )}
            </Field>

            <Field label="Type" required>
              {(props) => (
                <Select
                  value={type}
                  onValueChange={(value) => setValue('type', value as LocationType)}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {TYPE_LABEL[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Address" error={errors.address?.message}>
              {(props) => (
                <Textarea
                  {...props}
                  {...register('address', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                  rows={2}
                />
              )}
            </Field>

            <Field label="Phone" error={errors.phone?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('phone', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                  invalid={!!errors.phone}
                />
              )}
            </Field>

            <Field label="Note" error={errors.note?.message}>
              {(props) => (
                <Textarea
                  {...props}
                  {...register('note', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                  rows={2}
                />
              )}
            </Field>

            <FulfillmentFields
              offersPickup={watch('offersPickup') ?? false}
              offersShipping={watch('offersShipping') ?? false}
              onChange={(patch) => {
                if (patch.offersPickup !== undefined) setValue('offersPickup', patch.offersPickup)
                if (patch.offersShipping !== undefined)
                  setValue('offersShipping', patch.offersShipping)
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Create location
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditLocationDialog({
  location,
  onClose,
}: {
  location: LocationRow | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [type, setType] = useState<LocationType>('store')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [note, setNote] = useState('')
  const [offersPickup, setOffersPickup] = useState(false)
  const [offersShipping, setOffersShipping] = useState(false)

  // Reset the form whenever a different location is opened; without this the
  // previous location's values would linger.
  useEffect(() => {
    if (!location) return
    setName(location.name)
    setType(location.type)
    setAddress(location.address ?? '')
    setPhone(location.phone ? formatUsPhone(location.phone) : '')
    setNote(location.note ?? '')
    setOffersPickup(location.offersPickup)
    setOffersShipping(location.offersShipping)
  }, [location])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/locations/${location?.id}`, {
        name,
        type,
        address: address.trim() || null,
        phone: phone.trim() || null,
        note: note.trim() || null,
        offersPickup,
        offersShipping,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.locations() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={location !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {location?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>

          <Field label="Type" required>
            {(props) => (
              <Select value={type} onValueChange={(value) => setType(value as LocationType)}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Address">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            )}
          </Field>

          <Field label="Phone">
            {(props) => (
              <Input {...props} value={phone} onChange={(e) => setPhone(e.target.value)} />
            )}
          </Field>

          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>

          <FulfillmentFields
            offersPickup={offersPickup}
            offersShipping={offersShipping}
            onChange={(patch) => {
              if (patch.offersPickup !== undefined) setOffersPickup(patch.offersPickup)
              if (patch.offersShipping !== undefined) setOffersShipping(patch.offersShipping)
            }}
          />

          <p className="font-identifier text-2xs text-foreground-muted">
            {location?.key} (key cannot be changed)
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * What the website may do with this location's stock.
 *
 * Phrased as two things the shop does rather than as one "sells online"
 * switch, because they are genuinely different commitments: a warehouse can
 * post an order out without ever being somewhere a customer may walk into.
 * Both off is the default, and means the stock here is internal.
 */
function FulfillmentFields({
  offersPickup,
  offersShipping,
  onChange,
}: {
  offersPickup: boolean
  offersShipping: boolean
  onChange: (patch: { offersPickup?: boolean; offersShipping?: boolean }) => void
}) {
  return (
    <fieldset className="space-y-3 rounded-md border border-border p-3">
      <legend className="px-1 text-2xs font-medium uppercase tracking-wide text-foreground-muted">
        Website
      </legend>

      <div className="flex items-start gap-3">
        <Switch
          id="offers-pickup"
          checked={offersPickup}
          onCheckedChange={(checked) => onChange({ offersPickup: checked })}
        />
        <label className="text-sm" htmlFor="offers-pickup">
          Customers can collect from here
          <span className="block text-xs text-foreground-muted">
            Shown as a collection point at checkout.
          </span>
        </label>
      </div>

      <div className="flex items-start gap-3">
        <Switch
          id="offers-shipping"
          checked={offersShipping}
          onCheckedChange={(checked) => onChange({ offersShipping: checked })}
        />
        <label className="text-sm" htmlFor="offers-shipping">
          Orders can be delivered from here
          <span className="block text-xs text-foreground-muted">
            Stock here is used to fill delivered orders.
          </span>
        </label>
      </div>

      {!offersPickup && !offersShipping ? (
        <p className="text-xs text-foreground-muted">
          With both off, nothing standing here counts as available on the website.
        </p>
      ) : null}
    </fieldset>
  )
}
